import type { AxiosError, AxiosResponse } from "axios";
import {
  getCookie,
  getFromLocalStorage,
  hasWindow,
  isDebugAPI,
  logErrorToSentry,
  saveToLocalStorage,
} from "@litres/utils";
import type {
  IGlobalApiParams,
  IReduxRequestNode,
  ICSRFResponse,
  ICSRFTokensResponse,
  IApiError,
} from "@litres-libs/types";
import axios from "../../instance/axios";
import { hash } from "./../utils/object-hash";
import Request from "../Request";
import type { ICsrfConfig } from "../config/csrf";
import { csrfConfig } from "../config/csrf";
import { parseGUAjaxJSONfromHTML } from "../utils/GUAjaxUtils";
import { TroublePopup } from "../utils/TroublePopup";
import { ApiError, ApiType } from "../../types/api";

const DEFAULT_CSRF_TOKENS_LENGTH = 6; // Какое количество csrf токенов нужно запросить если у нас их нет
const REQUEST_MIN_TIMEOUT = 500; // Через сколько времени делать новый ретрай запроса
const TIMEOUT_RETRY_FACTOR = 2; // На сколько можнить время до новогог ретрая в случае повтора ретрая
const MAX_RETRY_REQUESTS = 4; // Максимум ретраев до того, как отобразить ошибку запроса
const MAX_FREE_TOKENS_REQUEST = 1; // Установим лимит на запрос через ручку получения токенов

type CSRFToken = [string, number];
type CSRFTokensStorage = CSRFToken[];

/**
 * Провайдер запросов CSRF/GUJ.
 * Создает синглтон, чтобы все запросы шли через 1 точку
 */
export class CSRFPromiseProvider<TParams, TPayload> {
  private constructor(
    config: ICsrfConfig,
    initialSID: string | undefined,
    initialCSRFArray: [string, number][] | undefined
  ) {
    this.SID = initialSID || undefined;
    this.baseURL = config.baseURL || "DEFAULT_BASE_URL";
    this.tokensArray = initialCSRFArray || []; // Массив CSRF токенов, которые расходуются на запросы
    this.troublePopup = null;
    this.requestsHashtable = {}; // Запросы кладутся в hash-table, который хранит в себе количество перезапросов и их содержимое по одному запросу
    this.requestFreeTokensCounter = 0;
    // При инициализации вызываем проверку токенов на устарелость. Те что устарели - вычистим из массива
    this.checkTokensTimeAndClean();
  }

  private SID: string | undefined;

  private baseURL: string;

  private tokensArray: CSRFTokensStorage;

  public troublePopup: TroublePopup | null;

  private static instance: CSRFPromiseProvider<any, any>;

  private requestsHashtable: {
    [hash: string]: {
      retryTimeout: number;
      retryNumber: number;
      requestKeyID: string;
      requestParams: IGlobalApiParams<TParams>;
    };
  };

  private requestFreeTokensCounter: number;

  /**
   * Метод, возвращающий единичный экземпляр синглтона
   * @returns Возвращает провайдер каталита для вызова запросов
   */
  public static getInstance<TParams, TPayload>(): CSRFPromiseProvider<
    TParams,
    TPayload
  > {
    // Передачу конфига не делаем, рассчитываем что мы работаем с одним каталитом по одному конфигу
    // По умолчанию берем SID из куки, но в дальнейшем дадим делать запрос от сида в параметрах запроса
    const initialSID = getCookie("SID");

    // Брать CSRF из локалстораджа мы можем только на того же юзера, что их запросил
    // Для другого юзера надо удалить массив токенов другого пользователя
    const isCSRFUserPrevious =
      hasWindow && window.localStorage
        ? getFromLocalStorage("litres.csrf.user") === initialSID
        : false;

    if (!CSRFPromiseProvider.instance || !isCSRFUserPrevious) {
      // Пробуем получить массив CSRF из локалстораджа
      const csrfInLocalStorage =
        hasWindow && window.localStorage
          ? getFromLocalStorage<string>("litres.csrf")
          : null;

      // Аккуратно парсим
      let parsedCSRFArray = [] as CSRFTokensStorage;
      if (isCSRFUserPrevious && csrfInLocalStorage) {
        try {
          parsedCSRFArray = JSON.parse(csrfInLocalStorage);
        } catch (error) {
          if (isDebugAPI) {
            console.error("Не удалось спарсить CSRF из localStorage:", error);
          }
          saveToLocalStorage({ "litres.csrf": [] });
          parsedCSRFArray = [];
        }
      } else {
        // Если пользователь сменился - установим пустой массив CSRF токенов, в первый запрос их затянет экшн get_some_csrf
        saveToLocalStorage({ "litres.csrf": [] });
        parsedCSRFArray = [];
      }

      CSRFPromiseProvider.instance = new CSRFPromiseProvider<TParams, TPayload>(
        csrfConfig,
        initialSID,
        parsedCSRFArray
      );
    }

    return CSRFPromiseProvider.instance;
  }

  /**
   * Функция зарегистрирует запров в объекте явзяющимся хеш-таблицой и установит ему нулевые дефолтные значения.
   * Данная таблица нужна чтобы иметь счетчики для каждого запроса - количество перезапросов, текущий размер таймаута для ретраев и прочее, накапливающееся для запроса
   * @param params - параметры вызванного запроса
   */
  private registerRequestInHashtable = (
    params: IGlobalApiParams<TParams>
  ): void => {
    // Абстракция хранит объект, в котором ключ - хеш запроса, а в параметрах данные конретно этого запроса (количество ретраев, ошибок и прочее храним там)
    // Зарегистрируем там этот запрос если его нет. А если есть -повысим счетчики
    const REQUEST_HASH_ID = hash({
      SID: this.SID,
      params: params,
    });

    // Если мы не находим в хеш-таблице ключ этого запроса - значит нужно создать значение и установить счетчики запросов на ноль
    if (!this.requestsHashtable[REQUEST_HASH_ID]) {
      this.requestsHashtable[REQUEST_HASH_ID] = {
        retryNumber: 0,
        retryTimeout: REQUEST_MIN_TIMEOUT,
        requestKeyID: params.provider.id,
        requestParams: params,
      };
    }
  };

  /**
   * Функция для повтора вызова запроса. В основном используется для известных ошибок, где мог затупить сервер
   * @returns - функция возвращает промис-копию текущего запроса
   */
  public retryRequest = async (
    REQUEST_HASH_ID: string
  ): Promise<IReduxRequestNode<TParams, TPayload>> => {
    const requestHash = this.requestsHashtable[REQUEST_HASH_ID];

    requestHash.retryNumber = requestHash.retryNumber + 1;
    requestHash.retryTimeout = requestHash.retryTimeout * TIMEOUT_RETRY_FACTOR;

    console.log(`Таймаут ${requestHash.retryTimeout}`);
    return await this.makeAxiosRequest(requestHash.requestParams);
  };

  /**
   * Данные в запрос передаются в виде FormData. (CSRF работает на этой механике)
   * Нам нужно взять параметры из кода в виде JSON, и сформировать из них эту FormData чтобы подписать запрос.
   * Также требуется подписать запрос через CSRF токен, который также добавляется в FormData
   * @param params - типовые параметры запроса
   * @returns FormData
   */
  private getFormdataFromParams = async (
    params: IGlobalApiParams<TParams>
  ): Promise<FormData> => {
    // GUJ отправляет данные запроса в виде formData, сформируем из объекта
    const bodyFormData = new FormData();
    const dataset = params.request?.data as Record<string, unknown>;

    if (typeof dataset !== "undefined") {
      const keys = Object.keys(dataset);
      keys.forEach(key => {
        bodyFormData.append(
          key,
          // @ts-ignore
          dataset[key] instanceof Blob ? dataset[key] : String(dataset[key])
        );
      });

      // Подписываем запрос токеном из хранилища
      const activeToken = await this.getActiveToken();
      if (activeToken.length) {
        bodyFormData.append("csrf", activeToken[0]);
      }
    }

    return bodyFormData;
  };

  /**
   * Основной метод вызова запроса
   * @param params - стандартные передаваемые параметры для всех запросов в любые наши API
   * @returns {Promise} - Возвращает пропис запроса каталита
   */
  public async makeAxiosRequest(
    params: IGlobalApiParams<TParams>
  ): Promise<IReduxRequestNode<TParams, TPayload>> {
    // Запомним хеш-адрес текущего запроса
    const REQUEST_HASH_ID = hash({ SID: this.SID, params: params });
    // Запишем текущий запрос в хеш-таблицу
    this.registerRequestInHashtable(params);

    const url =
      params.url === "/"
        ? this.baseURL + "/pages/ajax_empty2/"
        : params.url && params.url.indexOf("http") === -1
          ? `${this.baseURL}${
              params.url === "/pages/ajax_empty2/"
                ? "/pages/ajax_empty2/"
                : params.url
            }`
          : params.url;

    const method = params.request ? params.request.method : "POST";
    const data = await this.getFormdataFromParams(params);
    const _params = params.request ? params.request.params : {};

    // Делаем запрос
    const res = axios({
      url,
      method,
      data,
      params: _params,
    })
      .then(
        async (
          response: AxiosResponse<ICSRFResponse<TPayload> | ICSRFTokensResponse>
        ) => {
          const responseJson = parseGUAjaxJSONfromHTML<TPayload>(response.data);

          if (responseJson?.csrf) {
            this.tokensArray.push([
              String(responseJson.csrf),
              new Date().getTime(),
            ]);
            this.saveTokens(this.tokensArray);
          }

          // Если не смогли спарсить строку с html - бросим обшибку
          if (responseJson === null) {
            throw {
              request: params,
              response: {
                status: 0,
                statusText: "Ответ не найден",
              },
            } as unknown as Error;
          }

          if (
            hasWindow &&
            (responseJson.need_captcha ||
              responseJson.actions_output?.need_captcha)
          ) {
            this.troublePopup = new TroublePopup({
              // УБрал передачу лоадер, так как в TroublePopup не было использования
              // loader: Loader,
              reason: "captcha",
            });

            this.troublePopup.init();

            // Временное решение - при показе капчи фейлим запрос
            throw {
              request: params,
              response: {
                status: "need_captcha",
                statusText: null,
              },
            } as unknown as Error;
          }

          // Если в JSON нашли признак ошибки GUAjax - бросим ошибку
          if (responseJson?.error_code) {
            // Если у нас бэк ответил csrf_retry - почистим токены, сделаем перезапрос
            if (responseJson.error_code === "csrf_retry") {
              if (
                this.requestsHashtable[REQUEST_HASH_ID].retryNumber <
                MAX_RETRY_REQUESTS
              ) {
                return new Promise(ful => {
                  setTimeout(async () => {
                    //Сообщим в консоль что у нас токен сдохший был
                    console.error(
                      `Произошла ошибка в запросе ${
                        params.provider.id
                      }, делаю ретрай №${
                        this.requestsHashtable[REQUEST_HASH_ID].retryNumber + 1
                      }`
                    );
                    const retry = await this.retryRequest(REQUEST_HASH_ID);

                    ful(retry);
                  }, this.requestsHashtable[REQUEST_HASH_ID].retryTimeout);
                });
              }
            }

            // Если это не из кейсов конда ошибка нам явно понятна - выкинем ошибку
            throw {
              request: params,
              response: {
                status: responseJson.error_code,
                statusText: responseJson.error_msg,
              },
            } as unknown as Error;
          }

          // Если в JSON нашли признак ошибки GUAjax - бросим ошибку (если нет редиректа, иначе вернем ответ в onSuccess)
          if (responseJson?.error_msg && !responseJson.redirect) {
            throw {
              request: params,
              response: {
                status:
                  responseJson.actions_output &&
                  "error_code" in responseJson.actions_output
                    ? responseJson.actions_output.error_code
                    : responseJson.error_code,
                statusText: responseJson.error_msg,
              },
            } as unknown as Error;
          }

          if (isDebugAPI) {
            console.info(
              `%cRES: ${params.provider.id}`,
              "background: #abff5f; padding: 2px 12px; color: #848484",
              responseJson,
              params
            );
          }

          // Обнулим фейлы на этот запрос в хеш-таблице в случае успеха
          this.requestsHashtable[REQUEST_HASH_ID] = {
            retryNumber: 0,
            retryTimeout: 200,
            requestKeyID: params.provider.id,
            requestParams: params,
          };

          // Если передали коллбек onSuccess - вызовем его
          if (params.onSuccess) {
            params.onSuccess({
              request: params,
              response: responseJson,
              error: undefined,
            });
          }

          // Функции нужно удалить из объекта запроса, так как мы кладем эти данные в стор, и функции там не нужны
          delete params.onError;
          delete params.onSuccess;

          // В случае успешного ответа вернем ответ в fullfill
          return {
            request: params,
            response: responseJson,
            error: undefined,
          };
        }
      )
      .catch(async (error: AxiosError<IApiError>) => {
        if (error.response?.status === 500) {
          logErrorToSentry(new ApiError("API Error (500)"), {
            contextProperties: [
              "REQUEST_PARAMS",
              {
                url,
                method,
                params: JSON.stringify(_params),
                data: JSON.stringify(data),
              },
            ],
            tags: {
              api_name: ApiType.CSRF,
            },
          });

          if (
            this.requestsHashtable[REQUEST_HASH_ID].retryNumber <
            MAX_RETRY_REQUESTS
          ) {
            return new Promise(ful => {
              setTimeout(async () => {
                //Сообщим в консоль что у нас токен сдохший был
                console.error(
                  `Произошла ошибка в запросе ${
                    params.provider.id
                  }, делаю ретрай №${
                    this.requestsHashtable[REQUEST_HASH_ID].retryNumber + 1
                  }`
                );
                const retry = await this.retryRequest(REQUEST_HASH_ID);

                ful(retry);
              }, this.requestsHashtable[REQUEST_HASH_ID].retryTimeout);
            });
          } else if (
            this.requestsHashtable[REQUEST_HASH_ID].requestKeyID === "login" &&
            this.requestsHashtable[REQUEST_HASH_ID].retryNumber >=
              MAX_RETRY_REQUESTS
          ) {
            if (hasWindow) {
              // Костыль на зависающий логин в случае если сервер перл выдал 500-ку пытась смержить данные юзера от ангера регу
              window.location.reload();
            }
          }
        }

        if (isDebugAPI) {
          console.error(
            `%cERR: ${params.provider.id}`,
            "background: #f55e5e; padding: 2px 12px; color: #fff",
            error
          );
        }

        if (params.onError) {
          params.onError({
            request: params,
            error: {
              status: error.response?.status,
              statusText: error.response?.statusText,
            },
          });
        }

        // Функции нужно удалить из объекта запроса, так как мы кладем эти данные в стор, и функции там не нужны
        delete params.onError;
        delete params.onSuccess;

        return {
          request: params,
          error: {
            status: error.response?.status,
            statusText: error.response?.statusText,
          },
        };
      });

    // Требуется обработка внутри промиса для того, чтобы дождаться конца ретраев
    return new Promise((ful, rej) => {
      res.then(resp => {
        ful(resp as Promise<IReduxRequestNode<TParams, TPayload>>);
      });
      res.catch(() => {
        rej();
      });
    });
  }

  /**
   * Метод запросит у сервера массив новых CSRF токенов
   */
  public async requestFreeTokens(): Promise<CSRFTokensStorage> {
    const tokensArray = [] as CSRFTokensStorage;
    this.requestFreeTokensCounter = this.requestFreeTokensCounter + 1;

    try {
      const response = new Request.GUJ<{ num: number }, ICSRFTokensResponse>({
        url: "/pages/get_some_csrfs/",
        provider: {
          id: "GET_SOME_CSRFS",
        },
        request: {
          method: "POST",
          data: {
            num: DEFAULT_CSRF_TOKENS_LENGTH,
          },
        },
      });

      const data = await response.fetch();
      const tokens = data.response?.csrfs?.csrf;

      if (tokens && tokens.length > 0) {
        tokens.forEach((token: string): void => {
          tokensArray.push([token, new Date().getTime()]);
        });
      }
    } catch (error) {
      if (isDebugAPI) {
        console.error(
          "Не удалось спарсить CSRF через ручку get_some_csrf:",
          error
        );
      }
    }

    return tokensArray;
  }

  /**
   * Метод, прозходящий все хранилище и находящий устаревшие токены доступа
   */
  public checkTokensTimeAndClean(): void {
    const activeTokens = this.tokensArray.filter(
      (token: [string, number]) => Number(new Date()) - token[1] < 86400000
    );
    this.saveTokens(activeTokens);
  }

  /**
   * Сохранение токенов в хранилище
   * @param tokens
   */
  public saveTokens(tokens: [string, number][]): void {
    this.tokensArray = tokens;
    saveToLocalStorage({ "litres.csrf.user": this.SID || "" });
    saveToLocalStorage({ "litres.csrf": this.tokensArray });
  }

  /**
   * Метод, достающий токен из хранилища и проверяющий его на актуальность, либо запрашивающий новых токенов
   * @returns token pair
   */
  public async getActiveToken(): Promise<CSRFToken> {
    // Если у нас массив токенов вообще пустой - запросим список новых через ручку
    if (this.tokensArray.length === 0) {
      if (this.requestFreeTokensCounter < MAX_FREE_TOKENS_REQUEST) {
        this.saveTokens(await this.requestFreeTokens());
      } else {
        // ВЫХОД ИЗ РЕКУРСИИ
        // Достигнут лимит перезапросов CSRF токенов, в хранилище кладем токен-ошибку
        // Далее произойдет следующее - произойдет попатка сделать запрос с этим заранее неправильным токеном
        // Сервер ответит либо csrf_retry и даст заменный токен, который уже позволит сделать запрос
        // Либо по достижении лимита на перезапрос для текущего запроса выбросит реальную ошибку в код
        return [
          "ERROR: REACHED_LIMIT_FREE_TOKENS_REQUEST",
          new Date().getTime(),
        ];
      }
    }

    if (this.tokensArray.length > 0) {
      // Берем первый токен в массиве, чтобы посмотреть его дату, не устарел ли он
      const storeToken = this.tokensArray.shift();
      if (storeToken?.[1] && Number(new Date()) - storeToken[1] < 86400000) {
        return storeToken;
      }
    }

    // Если не удалось - удалим этот токен и возьмем другой по рекурсии
    return await this.getActiveToken();
  }
}
